<template>
    <div class="relative">
        <v-text-field
            :label="label"
            v-model="password"
            :rules="[rules.required, rules.length]"
            :append-inner-icon="isPasswordVisible ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="togglePasswordVisibility"
            :type="isPasswordVisible ? 'text' : 'password'"
            outlined
            dense
            @input="onPasswordInput"
            :error="hasError"
        />
        
        <!-- 仅当没有验证错误时显示密码强度提示 -->
        <div class="strength-indicator" v-if="!hasError">
            <div class="indicator-dot" :style="{ backgroundColor: passwordStrength.colorCode }"></div>
            <span class="indicator-text">{{ passwordStrength.message }}</span>
        </div>
    </div>
</template>

<script lang="ts" setup>
const props = defineProps<{
    modelValue: string;
    label: string;
}>();

const emit = defineEmits(['update:modelValue']);

const isPasswordVisible = ref(false);
const password = ref(props.modelValue);

const passwordStrength = ref({
    colorCode: 'transparent',
    message: '',
});

const hasError = ref(false); // 标识是否有验证错误

const rules = {
    required: (v: string) => !!v || 'This field is required',
    length: (v: string) => (v.length >= 8 && v.length <= 20) || 'Password must be between 8 and 20 characters',
};

// 切换密码可见性
function togglePasswordVisibility() {
    isPasswordVisible.value = !isPasswordVisible.value;
}

// 当密码输入时，更新强度并发出事件更新父组件的 v-model
function onPasswordInput() {
    checkPasswordStrength();
    checkValidation(); // 在输入时检查验证错误
    emit('update:modelValue', password.value); // 触发 v-model 绑定更新
}

// 检查密码强度
function checkPasswordStrength() {
    const pass = password.value;
    let strength = 0;

    if (pass.length >= 8) strength += 1;
    if (/[A-Z]/.test(pass)) strength += 1;
    if (/[a-z]/.test(pass)) strength += 1;
    if (/[0-9]/.test(pass)) strength += 1;
    if (/[\W_]/.test(pass)) strength += 1;

    switch (strength) {
        case 1:
            passwordStrength.value = { colorCode: '#f44336', message: 'Weak' };
            break;
        case 2:
            passwordStrength.value = { colorCode: '#ff9800', message: 'Average' };
            break;
        case 3:
            passwordStrength.value = { colorCode: '#ffeb3b', message: 'Strong' };
            break;
        case 4:
        case 5:
            passwordStrength.value = { colorCode: '#4caf50', message: 'Excellent' };
            break;
        default:
            passwordStrength.value = { colorCode: 'transparent', message: '' };
    }
}

// 检查验证错误
function checkValidation() {
    const validationResults = [rules.required(password.value), rules.length(password.value)];
    hasError.value = validationResults.some((result) => result !== true);
}

// 监听父组件传入的 modelValue 变化，确保内部状态同步
watch(() => props.modelValue, (newValue) => {
    password.value = newValue;
});
</script>

<style lang="scss" scoped>
.strength-indicator {
    display: flex;
    position: absolute;
    bottom: 0;
    left: 10px;
    align-items: center;
}

.indicator-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 8px;
}

.indicator-text {
    font-size: 14px;
    color: #ffffff;
}
</style>
